import { useState, useEffect, useRef } from 'react';

interface UseLazyImageLoadOptions {
  threshold?: number;
  rootMargin?: string;
  placeholder?: string;
}

export const useLazyImageLoad = (
  src: string | undefined,
  options: UseLazyImageLoadOptions = {}
) => {
  const {
    threshold = 0.1,
    rootMargin = '1200px', // Load images 1200px before they enter viewport (very aggressive)
    placeholder = ''
  } = options;

  const [imageSrc, setImageSrc] = useState<string>(placeholder);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    if (!src || !imgRef.current) return;

    // Check if IntersectionObserver is supported
    if (!('IntersectionObserver' in window)) {
      // Fallback: load image immediately
      setImageSrc(src);
      return;
    }

    // Create observer
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Start loading the image
            setImageSrc(src);
            
            // Disconnect observer once image starts loading
            if (observerRef.current) {
              observerRef.current.disconnect();
            }
          }
        });
      },
      {
        threshold,
        rootMargin
      }
    );

    // Start observing
    observerRef.current.observe(imgRef.current);

    // Cleanup
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [src, threshold, rootMargin]);

  const handleImageLoad = () => {
    setImageLoaded(true);
    setImageError(false);
  };

  const handleImageError = () => {
    setImageError(true);
    setImageLoaded(false);
  };

  return {
    imgRef,
    imageSrc,
    imageLoaded,
    imageError,
    handleImageLoad,
    handleImageError
  };
};